import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ShieldCheck, Database, Upload, Undo2 } from 'lucide-react';

const STORAGE_KEY = 'feedoptimizer:safety-hint-shown';

export function useSafetyHint() {
  const [open, setOpen] = React.useState(false);

  const showIfFirstTime = React.useCallback(() => {
    if (!localStorage.getItem(STORAGE_KEY)) {
      setOpen(true);
      localStorage.setItem(STORAGE_KEY, '1');
    }
  }, []);

  return { open, setOpen, showIfFirstTime };
}

interface SafetyHintDialogProps {
  open: boolean;
  onClose: () => void;
}

export const SafetyHintDialog: React.FC<SafetyHintDialogProps> = ({ open, onClose }) => {
  const { t } = useTranslation('dashboard');

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-success" />
            {t('safetyHint.title')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <p className="text-sm text-muted-foreground">
            {t('safetyHint.intro')}
          </p>

          <div className="space-y-3">
            <div className="flex gap-3">
              <Database className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <p className="text-sm">{t('safetyHint.localOnly')}</p>
            </div>

            <div className="flex gap-3">
              <Upload className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <p className="text-sm">{t('safetyHint.explicitUpload')}</p>
            </div>

            <div className="flex gap-3">
              <Undo2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <p className="text-sm">{t('safetyHint.easyRevert')}</p>
            </div>
          </div>
        </div>

        <Button onClick={onClose} className="w-full">
          {t('safetyHint.understood')}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
